import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { settingsAPI } from '../services/apiService';

const Impressum = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [impressumData, setImpressumData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchImpressumData();
  }, []);

  const fetchImpressumData = async () => {
    try {
      // Try to get custom impressum data from API
      const response = await settingsAPI.getImpressum();
      setImpressumData(response.data);
    } catch (error) {
      // Use default data if no custom data available
      setImpressumData(getDefaultImpressumData());
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultImpressumData = () => ({
    name: "Abdulla Abdullaev",
    company: "DElightX – Fotografie & Design",
    street: "Musterstraße 12",
    city: "07545 Gera",
    country: "Deutschland",
    phone: "+49 123 456789",
    email: "info@delightx.de",
    vatId: "DE123456789",
    responsiblePerson: "Abdulla Abdullaev"
  });

  const seoData = {
    title: currentLang === 'ru' ? 'Импрессум - FotografX.de' : 'Impressum - FotografX.de',
    description: currentLang === 'ru' ? 'Выходные данные и юридическая информация FotografX.de' : 'Impressum und rechtliche Angaben von FotografX.de',
    keywords: currentLang === 'ru' ? 'импрессум, контакты, fotografx' : 'Impressum, Kontakt, FotografX, Angaben'
  };

  if (isLoading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      <SEO {...seoData} />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 
            className="text-4xl md:text-5xl font-bold mb-6 text-center"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'ru' ? 'Импрессум' : 'Impressum'}
          </h1>
          <p className="text-xl text-gray-300 text-center max-w-2xl mx-auto">
            {currentLang === 'ru' 
              ? 'Юридическая информация и выходные данные'
              : 'Rechtliche Angaben und Informationen gemäß § 5 TMG'
            }
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-lg max-w-none">
            {currentLang === 'ru' ? (
              // Russian Content
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Информация об операторе согласно § 5 TMG:
                  </h2>
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
                    <p className="text-gray-300 leading-relaxed mb-2">
                      <strong className="text-white">{impressumData.name}</strong><br />
                      {impressumData.company}<br />
                      {impressumData.street}<br />
                      {impressumData.city}<br />
                      {impressumData.country}
                    </p>
                    <p className="text-gray-300 leading-relaxed mt-4">
                      <strong className="text-white">Телефон:</strong> {impressumData.phone}<br />
                      <strong className="text-white">E-Mail:</strong> {impressumData.email}<br />
                      <strong className="text-white">НДС ID:</strong> {impressumData.vatId}
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Ответственный за содержание согласно § 55 абз. 2 RStV:
                  </h2>
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
                    <p className="text-gray-300 leading-relaxed">
                      {impressumData.responsiblePerson}<br />
                      По указанному выше адресу.
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Исключение ответственности
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Ответственность за содержание</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Содержание наших страниц было создано с особой тщательностью. Однако мы не можем 
                        гарантировать правильность, полноту и актуальность содержания. Как поставщик услуг 
                        мы несем ответственность за собственное содержание этих страниц согласно § 7 абз. 1 TMG.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Ответственность за ссылки</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Наше предложение содержит ссылки на внешние веб-сайты третьих сторон, на содержание 
                        которых мы не имеем влияния. Поэтому мы не можем нести ответственность за это внешнее содержание.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Авторское право</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Содержание и работы на этих страницах, созданные операторами страниц, подлежат 
                        немецкому авторскому праву. Воспроизведение, обработка, распространение и любое 
                        коммерческое использование за пределами авторского права требуют письменного согласия автора.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-8">
                  <p className="text-gray-400 text-sm">
                    Статус: Январь 2025
                  </p>
                </div>
              </div>
            ) : (
              // German Content
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Angaben gemäß § 5 TMG:
                  </h2>
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
                    <p className="text-gray-300 leading-relaxed mb-2">
                      <strong className="text-white">{impressumData.name}</strong><br />
                      {impressumData.company}<br />
                      {impressumData.street}<br />
                      {impressumData.city}<br />
                      {impressumData.country}
                    </p>
                    <p className="text-gray-300 leading-relaxed mt-4">
                      <strong className="text-white">Telefon:</strong> {impressumData.phone}<br />
                      <strong className="text-white">E-Mail:</strong> {impressumData.email}<br />
                      <strong className="text-white">Umsatzsteuer-ID:</strong> {impressumData.vatId}
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:
                  </h2>
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-700">
                    <p className="text-gray-300 leading-relaxed">
                      {impressumData.responsiblePerson}<br />
                      unter der oben genannten Adresse.
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Haftungsausschluss
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Haftung für Inhalte</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                        Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. 
                        Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                        nach den allgemeinen Gesetzen verantwortlich.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Haftung für Links</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen 
                        Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                        Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der 
                        Seiten verantwortlich.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-white">Urheberrecht</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
                        dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
                        der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
                        Zustimmung des jeweiligen Autors bzw. Erstellers.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-8">
                  <p className="text-gray-400 text-sm">
                    Stand: Januar 2025
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impressum;